// src/config/urls.js

// Base URLs from environment variables
// Priority: NODE_ENV (for local dev) > REACT_APP_API_URL (for builds)
const getBaseUrl = () => {
  // For local development (npm start), always use localhost
  // This ensures npm start always connects to local backend
  if (process.env.NODE_ENV === 'development') {
    return 'http://127.0.0.1:8000';
  }
  
  // For builds (npm run build:dev, build:staging), use REACT_APP_API_URL from .env files
  // This allows different URLs for dev/staging/production builds
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // Default fallback (should not be reached if env vars are set correctly)
  return 'https://dev.api.dataportal.kaleideo.co';
};

export const BASE_URL = getBaseUrl();

// Authentication related endpoints
export const AUTH_URLS = {
    login: `${BASE_URL}/api/login`,
    register: `${BASE_URL}/api/register`,
    logout: `${BASE_URL}/api/logout`,
    refreshToken: `${BASE_URL}/api/refresh-token`,
    me: `${BASE_URL}/api/me`
};

// User management endpoints
export const USER_URLS = {
    getAllUsers: `${BASE_URL}/api/users`,
    getMe:`${BASE_URL}/api/me`,
    getUser: (userId) => `${BASE_URL}/api/users/${userId}`,
    updateUser: (userId) => `${BASE_URL}/api/users/${userId}`,
    updateUserRole: (userId) => `${BASE_URL}/api/users/${userId}/role`,
    updateUserStatus: (userId) => `${BASE_URL}/api/users/${userId}/status`,
    updateUserLastActive: (userId) => `${BASE_URL}/api/users/${userId}/last-active`,
    importUsers: `${BASE_URL}/api/users/import`
};

// Order management endpoints
export const ORDER_URLS = {
    // Draft orders
    createDraft: `${BASE_URL}/api/orders`,
    getDraft: (draftId) => `${BASE_URL}/api/draft_order/${draftId}`,
    // getFinal: (draftId) => `${BASE_URL}/api/orders/finalpage/${draftId}`,
    deleteDraft: (draftId) => `${BASE_URL}/api/orders/${draftId}`,
    getUserOrders: (userId) => `${BASE_URL}/api/orders?user_id=${userId}`,
    
    // Archival and tasking order specific endpoints
    feasibilityCheck: `${BASE_URL}/api/check_feasibilty`,
    checkSwaths: `${BASE_URL}/api/check_swaths`,
    availableScenes: `${BASE_URL}/api/available_scenes`,
    
    // Final order endpoints
    finalize: (draftId) => `${BASE_URL}/api/orders/finalize/${draftId}`,
    getFinal: (orderId) => `${BASE_URL}/api/orders/finalpage/${orderId}`,
    adminOrders: `${BASE_URL}/api/admin_orders`
};


// New catalog orders endpoints
export const CATALOG_URLS = {
    createOrder: `${BASE_URL}/api/catalog-orders/catalog-orders/`,
    getAllOrders: `${BASE_URL}/api/catalog-orders/catalog-orders/`,
    getAllOrdersAdmin: `${BASE_URL}/api/catalog-orders/admin/all-catalog-orders/`,
    getOrder: (orderId) => `${BASE_URL}/api/catalog-orders/catalog-orders/${orderId}`,
    updateOrder: (orderId) => `${BASE_URL}/api/catalog-orders/catalog-orders/${orderId}`,
    updateOrderStatus: (orderId) => `${BASE_URL}/api/catalog-orders/catalog-orders/${orderId}/status`,
    deleteOrder: (orderId) => `${BASE_URL}/api/catalog-orders/catalog-orders/${orderId}`,
    downloadProduct: (orderId, productId) => `${BASE_URL}/api/catalog-orders/${orderId}/products/${productId}/download`,

};
// Add this to your urls.js file
export const TASKING_URLS = {
    createOrder: `${BASE_URL}/api/catalog-orders/tasking-orders/`,
    getAllOrders: `${BASE_URL}/api/catalog-orders/tasking-orders/`,
    getOrder: (orderId) => `${BASE_URL}/api/catalog-orders/tasking-orders/${orderId}`,
    updateOrder: (orderId) => `${BASE_URL}/api/catalog-orders/tasking-orders/${orderId}`,
    updateOrderStatus: (orderId) => `${BASE_URL}/api/catalog-orders/tasking-orders/${orderId}/status`,
    deleteOrder: (orderId) => `${BASE_URL}/api/catalog-orders/tasking-orders/${orderId}`,
    downloadProduct: (orderId, productId) => `${BASE_URL}/api/catalog-orders/${orderId}/products/${productId}/download`
};

export const SATELLITE_URLS = {
    getTrack: `${BASE_URL}/api/satellites/track`
};
// Preview and data processing endpoints
export const IMAGE_URLS = {
    getPreview: (imageId) => `${BASE_URL}/api/preview/${imageId}`,
    getBestScenes: `${BASE_URL}/api/best-scenes`,
    downloadPreview: (imageId) => `${BASE_URL}/api/download-preview/${imageId}`
};

// Search and catalog endpoints
export const SEARCH_URLS = {
    catalog: `${BASE_URL}/api/search`,
    constellations: `${BASE_URL}/api/constellations`,
    checkFeasibility: `${BASE_URL}/api/check-feasibility`,
            downloadFeasibility: `${BASE_URL}/api/export-feasibility`,
            downloadFeasibilityXLSX: `${BASE_URL}/api/export-feasibility-xlsx`,
            downloadFeasibilityCSVCombined: `${BASE_URL}/api/export-feasibility-csv-combined`,
    getProducts: `${BASE_URL}/api/products`,
    getProductDetails: (productId) => `${BASE_URL}/api/products/${productId}`,
    getProductsByOrderType: (orderType) => `${BASE_URL}/api/products/by-order-type/${orderType}`
};

// AOI and file handling endpoints
export const AOI_URLS = {
    checkFile: `${BASE_URL}/api/aoi/check_file`,
    uploadImage: (userId, reqId) => 
        `${BASE_URL}/api/user/${userId}/upload-image?request_id=${reqId}`,
    geospatial: (draftId) => 
        `${BASE_URL}/api/draft_order/geospatial/${draftId}`,
    getSavedAOIs: `${BASE_URL}/api/saved-aois`,
    saveAOI: `${BASE_URL}/api/saved-aois`,
    deleteAOI: (aoiId) => `${BASE_URL}/api/saved-aois/${aoiId}`
};

// Utility function to build URLs with query parameters
export const buildUrl = (baseUrl, params = {}) => {
    const query = Object.entries(params)
        .filter(([_, value]) => value !== undefined && value !== null)
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
        .join('&');
    return query ? `${baseUrl}?${query}` : baseUrl;
};